/**
 * Schreibwege der Sammlung in Resources (F-17): erfassen, zuordnen, loesen
 * und alte Lesezeichen ins Wissen verschieben. Notizen und Gedanken bleiben
 * in ihren eigenen Tabellen - der Gedanke ist derselbe wie im Journal.
 */
import { db } from '@/db/schema';
import { alsUrl, createResource, lesbarerName, quickCapture } from '@/db/repositories/resources';
import { createBook } from '@/db/repositories/books';
import { createTask } from '@/db/repositories/tasks';
import { createProject } from '@/db/repositories/projects';
import { createGoal } from '@/db/repositories/goals';
import { rueckgaengigMachbar } from '@/db/repositories/papierkorb';
import { yearPeriod } from '@/domain/goals/period';
import { zuordnungFuer, type ZuordnungsWahl } from '@/domain/resources/sammlung';
import { typAusLink } from '@/domain/wissen';
import { nowInstant, today } from '@/lib/datetime';
import type { ID } from '@/domain/types';

export type SammelArt = 'notiz' | 'gedanke';

/**
 * Schnellerfassung: Text wird eine Notiz, ein einzelner Link ein
 * Wissenseintrag ("Will ich aufnehmen") - fremde Quellen gehoeren ins
 * Wissen, nicht in Resources (V-13).
 */
export async function erfassen(input: string, areaId: ID | null = null): Promise<{ art: 'notiz' | 'wissen'; id: ID } | null> {
  const text = input.trim();
  if (!text) return null;
  const url = alsUrl(text);
  if (url) {
    const book = await createBook({ title: lesbarerName(url), url: text, typ: typAusLink(text), areaIds: areaId ? [areaId] : [] });
    return { art: 'wissen', id: book.id };
  }
  return { art: 'notiz', id: (await quickCapture(text, areaId)).id };
}

/** Eine neue, leere Notiz mit Text - fuer "Neuer Gedanke" (V-12). */
export async function neueNotiz(text: string, areaId: ID | null = null): Promise<ID> {
  const [erste = '', ...rest] = text.trim().split('\n');
  return (await createResource({ title: erste.trim() || 'Ohne Titel', body: rest.join('\n').trim() || undefined, areaId })).id;
}

/**
 * Ordnet eine Notiz oder einen Gedanken zu (Regel aus F-11: Projekt ODER
 * Ziel, der Bereich folgt). `null` hebt jede Zuordnung auf. Gibt `false`
 * zurueck, wenn es den Eintrag oder das Ziel nicht (mehr) gibt.
 */
export async function zuordnen(art: SammelArt, id: ID, wahl: ZuordnungsWahl): Promise<boolean> {
  return db.transaction('rw', [db.resources, db.thoughts, db.projects, db.goals, db.areas], async () => {
    if (wahl?.art === 'bereich' && !(await db.areas.get(wahl.id))) return false;
    const projects = wahl?.art === 'projekt' ? await db.projects.where('id').equals(wahl.id).toArray() : [];
    const goals = wahl?.art === 'ziel' ? await db.goals.where('id').equals(wahl.id).toArray() : [];
    const felder = zuordnungFuer(wahl, projects, goals);
    if (!felder) return false;
    const ts = nowInstant();
    const setzen = (row: { areaId?: ID | null; projectId?: ID; goalId?: ID; updatedAt: string }) => {
      delete row.projectId;
      delete row.goalId;
      Object.assign(row, felder, { updatedAt: ts });
    };
    const n = art === 'notiz'
      ? await db.resources.where('id').equals(id).modify(setzen)
      : await db.thoughts.where('id').equals(id).modify(setzen);
    return n > 0;
  });
}

/**
 * Loest einen Eintrag von einem Bereich, Projekt oder Ziel. Von einem
 * Projekt oder Ziel geloest bleibt er im Bereich - so rutscht er nicht
 * unbemerkt aus gefilterten Ansichten.
 */
export async function loesen(art: SammelArt, id: ID, von: 'bereich' | 'projekt' | 'ziel'): Promise<void> {
  if (von === 'bereich') {
    await zuordnen(art, id, null);
    return;
  }
  const feld = von === 'projekt' ? 'projectId' : 'goalId';
  const ts = nowInstant();
  const weg = (row: { projectId?: ID; goalId?: ID; updatedAt: string }) => { delete row[feld]; row.updatedAt = ts; };
  if (art === 'notiz') await db.resources.where('id').equals(id).modify(weg);
  else await db.thoughts.where('id').equals(id).modify(weg);
}

/**
 * Altes Lesezeichen ins Wissen verschieben (F-17, Entscheidung Ole): legt
 * einen Wissenseintrag an und loescht die Ressource. Mit Anhaengen geht das
 * nicht - Wissenseintraege haben keine Dateien, die gingen sonst verloren.
 */
export async function insWissenVerschieben(resourceId: ID): Promise<ID | 'anhaenge' | null> {
  // Rueckgaengig machbar (F-45): Wissenseintrag weg, Lesezeichen zurueck.
  return rueckgaengigMachbar({ art: 'verschoben', tabelle: 'resources', zeileIds: [resourceId] }, () => db.transaction('rw', [db.resources, db.attachments, db.books], async () => {
    const r = await db.resources.get(resourceId);
    // Schul-Materialien (F-22) gehoeren zum Fach und bleiben dort.
    if (!r || r.kind !== 'link' || r.subjectId) return null;
    if ((await db.attachments.where('resourceId').equals(resourceId).count()) > 0) return 'anhaenge';
    const book = await createBook({
      title: r.title || (r.url ?? 'Ohne Titel'),
      ...(r.url ? { url: r.url, typ: typAusLink(r.url) } : {}),
      tags: r.tags,
      areaIds: r.areaId ? [r.areaId] : [],
      ...(r.projectId ? { projectIds: [r.projectId] } : {}),
      ...(r.goalId ? { goalIds: [r.goalId] } : {}),
      ...(r.body ? { notes: r.body } : {}),
    });
    await db.resources.delete(resourceId);
    return book.id;
  }));
}

/**
 * Notiz in eine Aufgabe, ein Projekt oder ein Ziel umwandeln - wie bei den
 * Gedanken (F-13). Titel wird Titel, der Text Notiz bzw. Beschreibung; die
 * Zuordnung wandert mit. Die Notiz bleibt stehen und zeigt danach dorthin.
 */
export async function notizUmwandeln(id: ID, art: 'aufgabe' | 'projekt' | 'ziel'): Promise<ID | null> {
  const r = await db.resources.get(id);
  if (!r) return null;
  if (r.umgewandelt) return r.umgewandelt.id;
  const titel = r.title.trim() || 'Ohne Titel';
  let neu: ID;
  if (art === 'aufgabe') {
    neu = (await createTask({
      title: titel, status: 'naechste', areaId: r.areaId,
      ...(r.projectId ? { projectId: r.projectId } : r.goalId ? { goalId: r.goalId } : {}),
      ...(r.body ? { notes: r.body } : {}),
    })).id;
  } else if (art === 'projekt') {
    neu = (await createProject({ title: titel, areaId: r.areaId, ...(r.goalId ? { goalId: r.goalId } : {}), ...(r.body ? { description: r.body } : {}) })).id;
  } else {
    const jahr = yearPeriod(Number(today().slice(0, 4)));
    neu = (await createGoal({ title: titel, horizon: 'jahr', periodStart: jahr.start, periodEnd: jahr.end, areaId: r.areaId, ...(r.body ? { why: r.body } : {}) })).id;
  }
  await db.resources.update(id, { umgewandelt: { art, id: neu }, updatedAt: nowInstant() });
  return neu;
}
